import {
  FinancialSupportCaseResultDto,
  SgmsPolicyFundMatchDto,
} from '@hello-unicorn/api-client';
import { FinancialSupportDraft } from '../models/financial-support.model';

export type ExperiencePolicyFund = {
  revisionId: string;
  name: string;
  summary: string;
  requirementChecks: string[];
  missingRequirements: string[];
};

export const experiencePolicyFunds: ExperiencePolicyFund[] = [
  {
    revisionId: 'experience-small-manufacturer-2026',
    name: '소공인특화자금',
    summary: '제조업 소공인 조건과 입력한 금융지원 목적을 함께 볼 때 적합성이 높습니다.',
    requirementChecks: ['업종조건 부합', '소공인 기준 부합 가능', '희망금액 조건 검토 가능'],
    missingRequirements: ['세부 신청요건 추가 확인 필요'],
  },
  {
    revisionId: 'experience-general-management-2026',
    name: '일반경영안정자금',
    summary: '일반 소상공인 경영안정 목적과 입력한 금융지원 목적이 유사합니다.',
    requirementChecks: ['업종조건 부합', '업력조건 부합'],
    missingRequirements: [],
  },
  {
    revisionId: 'experience-innovation-growth-2026',
    name: '혁신성장촉진자금',
    summary: '현재 입력한 정보만으로는 일부 추가 요건의 확인이 필요합니다.',
    requirementChecks: [],
    missingRequirements: ['스마트기술 도입 여부', '최근 매출성장 여부', '수출 실적 여부'],
  },
  {
    revisionId: 'experience-emergency-management-2026',
    name: '긴급경영안정자금',
    summary: '일시적인 경영애로에 해당하는지와 관련 증빙 요건을 확인하면 검토할 수 있습니다.',
    requirementChecks: ['소상공인 기준 부합 가능'],
    missingRequirements: ['경영애로 사유 및 증빙 확인 필요'],
  },
  {
    revisionId: 'experience-refinancing-2026',
    name: '대환대출',
    summary: '기존 고금리 대출의 보유 여부와 대환 대상 채무를 확인하면 금융부담 개선을 검토할 수 있습니다.',
    requirementChecks: ['희망금액 조건 검토 가능'],
    missingRequirements: ['기존 고금리 대출 및 대환 대상 확인 필요'],
  },
];

export const defaultPolicyFundMatches: SgmsPolicyFundMatchDto[] = [
  {
    policyFundRevisionId: 'sgms-general-management-r1',
    rank: 1,
    code: 'GENERAL_MANAGEMENT',
    fund: {
      code: 'GENERAL_MANAGEMENT',
      name: '일반경영안정자금',
      summary: '단기 운영자금 조달 · 가장 폭넓게 지원',
      tag: '운영자금',
      maxAmount: 70_000_000,
      minimumAmount: 10_000_000,
      amountStep: 1_000_000,
      annualInterestRate: 3.51,
      termMonths: 60,
      graceMonths: 24,
    },
    suitabilityPercent: 100,
    conditionMetCount: 3,
    conditionTotalCount: 3,
    taxWarning: false,
    isSelectable: true,
    status: 'PRIORITY_REVIEW',
    explanation: '운영 목적과 업력, 상시근로자 조건을 충족합니다.',
    requirementChecks: [],
    missingRequirements: [],
  },
  {
    policyFundRevisionId: 'sgms-growth-base-r1',
    rank: 2,
    code: 'GROWTH_BASE',
    fund: {
      code: 'GROWTH_BASE',
      name: '성장기반자금',
      summary: '설비 도입 및 사업 확장 · 장기 저리',
      tag: '시설자금·사업확장',
      maxAmount: 200_000_000,
      minimumAmount: 10_000_000,
      amountStep: 1_000_000,
      annualInterestRate: 3,
      termMonths: 96,
      graceMonths: 36,
    },
    suitabilityPercent: 67,
    conditionMetCount: 2,
    conditionTotalCount: 3,
    taxWarning: false,
    isSelectable: true,
    status: 'REVIEWABLE',
    explanation: '사업 확장과 시설 투자 목적에 적합한 조건입니다.',
    requirementChecks: [],
    missingRequirements: ['세부 매출 요건 확인'],
  },
  {
    policyFundRevisionId: 'sgms-restart-special-r1',
    rank: 3,
    code: 'RESTART_SPECIAL',
    fund: {
      code: 'RESTART_SPECIAL',
      name: '재도전특별자금',
      summary: '경영애로 해소 및 기존 채무 조정',
      tag: '경영애로·대환',
      maxAmount: 60_000_000,
      minimumAmount: 10_000_000,
      amountStep: 1_000_000,
      annualInterestRate: 3.21,
      termMonths: 60,
      graceMonths: 24,
    },
    suitabilityPercent: 67,
    conditionMetCount: 2,
    conditionTotalCount: 3,
    taxWarning: false,
    isSelectable: true,
    status: 'REVIEWABLE',
    explanation: '경영애로 해소와 기존 채무 조정 목적을 함께 검토할 수 있습니다.',
    requirementChecks: [],
    missingRequirements: ['채무 현황 세부 확인'],
  },
  {
    policyFundRevisionId: 'sgms-credit-vulnerable-r1',
    rank: 4,
    code: 'CREDIT_VULNERABLE',
    fund: {
      code: 'CREDIT_VULNERABLE',
      name: '신용취약 소상공인자금',
      summary: '신용취약 구간 대상 긴급 운영자금',
      tag: '운영자금',
      maxAmount: 30_000_000,
      minimumAmount: 10_000_000,
      amountStep: 1_000_000,
      annualInterestRate: 2.5,
      termMonths: 60,
      graceMonths: 24,
    },
    suitabilityPercent: 67,
    conditionMetCount: 2,
    conditionTotalCount: 3,
    taxWarning: false,
    isSelectable: true,
    status: 'REVIEWABLE',
    explanation: '신용정보와 운영자금 목적을 기준으로 검토할 수 있습니다.',
    requirementChecks: [],
    missingRequirements: ['신용 구간 세부 확인'],
  },
  {
    policyFundRevisionId: 'sgms-youth-employment-r1',
    rank: 5,
    code: 'YOUTH_EMPLOYMENT',
    fund: {
      code: 'YOUTH_EMPLOYMENT',
      name: '청년고용연계자금',
      summary: '고용 유지·확대와 연계된 성장자금',
      tag: '운영자금·사업확장',
      maxAmount: 100_000_000,
      minimumAmount: 10_000_000,
      amountStep: 1_000_000,
      annualInterestRate: 2.9,
      termMonths: 60,
      graceMonths: 24,
    },
    suitabilityPercent: 67,
    conditionMetCount: 2,
    conditionTotalCount: 3,
    taxWarning: false,
    isSelectable: true,
    status: 'REVIEWABLE',
    explanation: '대표자 연령과 고용 현황을 기준으로 검토할 수 있습니다.',
    requirementChecks: [],
    missingRequirements: ['고용 증가 요건 확인'],
  },
];

const roundToOne = (value: number) => Math.round(value * 10) / 10;
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const stabilityGrade = (score: number) =>
  score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : score >= 20 ? 'D' : 'E';
const reviewForRisk = (riskScore: number) => {
  if (riskScore <= 1.9) return ['적극검토', '검토 가능', 'A'] as const;
  if (riskScore <= 3.3) return ['정상검토', '검토 가능', 'B'] as const;
  if (riskScore <= 4.8) return ['조건부검토', '추가 확인 후 검토 가능', 'C'] as const;
  if (riskScore <= 6.4) return ['신중검토', '신중한 검토 필요', 'D'] as const;
  return ['보류권고', '현재 조건에서는 검토 어려움', 'E'] as const;
};

export const createFinancialSupportExperienceResult = (
  draft: FinancialSupportDraft,
): FinancialSupportCaseResultDto => {
  const revenue2025 = draft.revenue2025 ?? draft.annualRevenue;
  const revenue2025SamePeriod = draft.revenue2025SamePeriod ?? revenue2025;
  const revenue2026Ytd = draft.revenue2026Ytd ?? revenue2025;
  const employeeCount2026 = draft.employeeCount2026 ?? draft.employeeCount;
  const monthlyExpense = draft.monthlyEssentialExpense2026 ?? draft.monthlyEssentialExpense;
  const currentFinancialBalance = draft.currentFinancialBalance ?? 0;
  const monthlyRepayment = draft.monthlyRepayment ?? 0;
  const runwayBefore = roundToOne(draft.availableCash / monthlyExpense);
  const salesGrowthRate = revenue2025SamePeriod
    ? (revenue2026Ytd - revenue2025SamePeriod) / revenue2025SamePeriod
    : 0;
  const financialBalanceRatio = revenue2025
    ? currentFinancialBalance / revenue2025
    : 0;
  const repaymentBurdenRatio = revenue2025
    ? (monthlyRepayment * 12) / revenue2025
    : 0;
  const closeStabilityScore = clamp(
    Math.round(
      55
        + Math.min(runwayBefore, 6) * 6
        + clamp(salesGrowthRate * 100, -15, 15)
        - Math.min(financialBalanceRatio * 40, 25),
    ),
    0,
    100,
  );
  const repayStabilityScore = clamp(
    Math.round(
      90
        - Math.min(financialBalanceRatio * 50, 35)
        - Math.min(repaymentBurdenRatio * 100, 30)
        - (draft.cumulativeDelinquencyCount ?? 0) * 5
        - (draft.delinquentNow ? 25 : 0),
    ),
    0,
    100,
  );
  const riskScore = roundToOne(
    clamp(
      ((100 - closeStabilityScore) + (100 - repayStabilityScore)) / 25,
      0,
      8.8,
    ),
  );
  const [internalReviewLabel, publicReviewLabel, reviewGrade] =
    reviewForRisk(riskScore);
  const amounts = [30000000, 50000000, 80000000];
  const simulations = amounts.map((requestedAmount, index) => ({
    id: `experience-simulation-${requestedAmount}`,
    isRequested: requestedAmount === 50000000,
    requestedAmount,
    runwayBefore,
    runwayImmediatelyAfter: roundToOne(
      (draft.availableCash + requestedAmount) / monthlyExpense,
    ),
    monthlyRepaymentAfterGrace: [1100000, 1700000, 2700000][index],
    totalMonthlyRepayment:
      monthlyRepayment + [1100000, 1700000, 2700000][index],
    financialBalanceRatio: revenue2025
      ? (currentFinancialBalance + requestedAmount) / revenue2025
      : 0,
    repaymentBurden: ['낮음', '보통', '높음'][index],
    reviewGrade: ['B+', 'B', 'C'][index],
    stabilityAfter24Months: ['보통 이상', '보통 이상', '확인 필요'][index],
    timeline: {},
  }));
  const selectedSimulation = simulations[1];

  return {
    case: {
      id: 'experience-case',
      companyName: draft.companyName,
      businessForm: draft.businessForm || null,
      industryName: draft.industryName,
      industryCode: draft.industryCode || null,
      businessAgeMonths: draft.businessAgeYears * 12,
      openedAt: draft.openedAt || null,
      businessRegion: draft.businessRegion || null,
      businessDistrict: draft.businessDistrict || null,
      annualRevenue: revenue2025,
      revenue2023: draft.revenue2023,
      revenue2024: draft.revenue2024,
      revenue2025,
      revenue2025SamePeriod,
      revenue2026Ytd,
      operatingProfit2025: draft.operatingProfit2025,
      employeeCount: employeeCount2026,
      employeeCount2025: draft.employeeCount2025,
      employeeCount2026,
      availableCash: draft.availableCash,
      monthlyEssentialExpense: monthlyExpense,
      monthlyEssentialExpense2025: draft.monthlyEssentialExpense2025,
      monthlyEssentialExpense2026: monthlyExpense,
      currentFinancialBalance,
      monthlyRepayment,
      cbScore: draft.cbScore,
      sohoScore: draft.sohoScore,
      grantBenefits: [],
      purpose: draft.purpose,
      requestedAmount: draft.requestedAmount,
      currentRunwayMonths: runwayBefore,
    },
    policyBasis: {
      name: '소상공인시장진흥공단 정책자금',
      noticeTitle: '2026년 정책기준',
      referenceDate: '2026.07.30',
    },
    similarCompany: {
      id: 'experience-similar-company',
      criteria: {},
      sampleSize: 428,
      dataBasis: 'SIMULATED',
      sourceLabel: '유사기업 분석 데이터',
      referenceDate: '2026.07.30',
      normalRepaymentRate: 0.718,
      shortDelinquencyRate: 0.132,
      repeatedDelinquencyRate: 0.052,
      longDelinquencyRate: 0.084,
      accidentConversionRate: 0.018,
      closureRate: 0.066,
      averageSupportAmount: 48000000,
      averageRunwayBefore: 2.7,
      averageRunwayAfter: 6.8,
      runwayImprovementMonths: 4.1,
      averageRepaymentMonths: 48,
      postGraceDelinquencyRate: 0.093,
    },
    matches: experiencePolicyFunds.map((fund, index) => ({
      id: `experience-match-${index + 1}`,
      policyFundRevisionId: fund.revisionId,
      rank: index + 1,
      suitabilityScore: [92, 84, 61, 58, 52][index],
      status: [
        'PRIORITY_REVIEW',
        'REVIEWABLE',
        'NEEDS_CONFIRMATION',
        'NEEDS_CONFIRMATION',
        'NEEDS_CONFIRMATION',
      ][index],
      explanation: fund.summary,
      requirementChecks: fund.requirementChecks,
      missingRequirements: fund.missingRequirements,
      taxWarning: false,
    })),
    simulations,
    decision: {
      id: 'experience-decision',
      automaticDecision: false,
      reviewGrade,
      reviewLabel: publicReviewLabel,
      riskScore,
      internalReviewLabel,
      publicReviewLabel,
      closeStabilityScore,
      closeStabilityGrade: stabilityGrade(closeStabilityScore),
      repayStabilityScore,
      repayStabilityGrade: stabilityGrade(repayStabilityScore),
      recommendedAmount: selectedSimulation.requestedAmount,
      expectedRepaymentStability: stabilityGrade(repayStabilityScore),
      relativeSimilarCompanyRisk: riskScore <= 3.3 ? '낮음' : riskScore <= 4.8 ? '보통' : '높음',
      supportNeed: runwayBefore < 3 ? '높음' : runwayBefore < 6 ? '보통' : '낮음',
      supportFit: riskScore <= 3.3 ? '높음' : riskScore <= 4.8 ? '보통' : '낮음',
      dataCompleteness: 100,
      positiveFactors: [
        '금융지원 이후 예상 런웨이가 증가합니다.',
        '유사기업의 정상상환 비중이 상대적으로 높습니다.',
        '희망금액이 기업규모 대비 과도하지 않습니다.',
      ],
      confirmationFactors: [
        '현재 런웨이가 짧아 금융지원 지연 시 운영위험이 증가할 수 있습니다.',
        '거치기간 이후 현금흐름을 추가로 확인해야 합니다.',
      ],
      reviewerCheckpoints: [
        '자금 사용목적',
        '현재 런웨이',
        '매출 유지 가능성',
        '기존 금융부담',
      ],
      modelVersion: 'experience-simulation-2026.07',
    },
    states: {
      similarCompany: 'READY',
      policyMatch: 'READY',
      simulation: 'READY',
      decision: 'READY',
    },
  };
};
